import clsx from 'clsx'
import type {
  MouseEventHandler,
  ComponentProps,
  ReactNode,
} from 'react'
import { useCallback, useRef } from 'react'
import { useIntl } from 'react-intl'

import ResponsiveDropdownList from 'tripPlanner/components/Common/Dropdowns/ResponsiveDropdownList'
import LineEllipsisVIcon from 'LuxKit/Icons/line/LineEllipsisVIcon'
import useToggle from 'hooks/useToggle'
import IconButton from 'LuxKit/Button/IconButton'

type RenderableContent =
  | ReactNode
  | ((closeDropdown: () => void) => ReactNode)

interface Props {
  title: string
  subtitle?: string
  className?: string
  size?: ComponentProps<typeof IconButton>['size']
  shape?: ComponentProps<typeof IconButton>['shape']
  kind?: ComponentProps<typeof IconButton>['kind']
  bodyContent?: RenderableContent
  footerContent?: RenderableContent
}

export default function OptionsButton({
  className,
  title,
  subtitle,
  bodyContent,
  footerContent,
  size = 'small',
  shape = 'circle',
  kind = 'tertiary',
}: Props) {
  const intl = useIntl()

  const {
    value: dropdownOpen,
    toggle: toggleDropdown,
    off: closeDropdown,
  } = useToggle()
  const triggerRef = useRef<HTMLButtonElement>(null)

  const onContentClick: MouseEventHandler<HTMLDivElement> = useCallback(e => {
    // If user clicked a button or a link within the dropdown, close the dropdown
    if (e.target instanceof HTMLElement) {
      const linkOrButton = e.target.closest('button, a')
      if (e.currentTarget.contains(linkOrButton)) {
        closeDropdown()
      }
    }
  }, [closeDropdown])

  const renderContent = useCallback(
    (content?: RenderableContent) =>
      typeof content === 'function' ? content(closeDropdown) : content,
    [closeDropdown],
  )

  const toggleMenu: MouseEventHandler = useCallback(
    (e) => {
      e.preventDefault()
      e.stopPropagation()
      toggleDropdown()
    },
    [toggleDropdown],
  )

  return (
    <>
      <IconButton
        kind={kind}
        shape={shape}
        size={size}
        data-testid="action-menu-button"
        aria-label={intl.formatMessage({
          id: 'OSwxcyWZf8-szg3KOL5Q',
          defaultMessage: 'action menu',
          description: 'Accessible label for the document options menu button',
        })}
        onClick={toggleMenu}
        aria-expanded={dropdownOpen}
        className={clsx(className, 'no-print')}
        ref={triggerRef}
      >
        <LineEllipsisVIcon />
      </IconButton>
      <ResponsiveDropdownList
        size="S"
        triggerRef={triggerRef}
        anchorRef={triggerRef}
        open={dropdownOpen}
        onClose={closeDropdown}
        placement="bottom-end"
        portaled
        mobileTitle={title}
        mobileSubtitle={subtitle}
      >
        <div onClick={onContentClick}>
          {renderContent(bodyContent)}
          {renderContent(footerContent)}
        </div>
      </ResponsiveDropdownList>
    </>
  )
}
